// Site-wide user geolocation, captured once per session on first page load and
// shared across routes (external store → components subscribe reactively).
// Used to sort/label providers by distance. Silent on denial; never throws.

export type UserCoords = { lat: number; lng: number };

// Rough bounding box covering the continental US + AK/HI/PR (our provider
// data is US-only). Used to skip auto-applying "near me" for clearly
// non-US visitors, so we don't show a "3,000 mi away" badge or an empty
// radius search for someone browsing from abroad.
export function isLikelyUS(c: UserCoords): boolean {
  return c.lat >= 15 && c.lat <= 72 && c.lng >= -180 && c.lng <= -60;
}

const COORDS_KEY = "hx_geo_coords_v1";

/**
 * "idle" — nothing asked for yet. "locating" — prompt is up or the fix is in
 * flight. "granted" — we have coords. "denied" — the visitor said no, or the
 * device could not produce a fix. "unsupported" — no geolocation API at all.
 *
 * The UI needs these separated: a denial has to be visible and recoverable
 * (browsers make the prompt un-reaskable once dismissed, so the only honest
 * response is to tell the visitor where the setting lives), and that is
 * indistinguishable from "idle" if all we track is whether coords exist.
 */
export type GeoStatus = "idle" | "locating" | "granted" | "denied" | "unsupported";

let current: UserCoords | null = null;
let status: GeoStatus = "idle";
let hydrated = false;
const listeners = new Set<() => void>();

function readStored(): UserCoords | null {
  try {
    const raw = sessionStorage.getItem(COORDS_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as { lat?: unknown; lng?: unknown };
    if (typeof o.lat === "number" && typeof o.lng === "number") return { lat: o.lat, lng: o.lng };
  } catch { /* ignore */ }
  return null;
}

/** Current coords, or null. Reads sessionStorage once so a fresh route pickup works. */
export function getUserCoords(): UserCoords | null {
  if (!hydrated && typeof window !== "undefined") {
    current = readStored();
    hydrated = true;
  }
  return current;
}

export function subscribeUserCoords(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Status without triggering a permission prompt. */
export function getGeoStatus(): GeoStatus {
  if (getUserCoords()) return "granted";
  return status;
}

function notify() {
  listeners.forEach((l) => l());
}

function setStatus(s: GeoStatus) {
  status = s;
  notify();
}

function commit(c: UserCoords) {
  current = c;
  hydrated = true;
  status = "granted";
  try { sessionStorage.setItem(COORDS_KEY, JSON.stringify(c)); } catch { /* private mode */ }
  notify();
}

/** One in-flight request shared by every caller, so a click during the
 *  already-granted silent fetch doesn't open a second prompt. */
let inflight: Promise<UserCoords | null> | null = null;

function locate(): Promise<UserCoords | null> {
  if (inflight) return inflight;
  if (typeof window === "undefined" || !navigator.geolocation) {
    setStatus("unsupported");
    return Promise.resolve(null);
  }
  setStatus("locating");
  inflight = new Promise<UserCoords | null>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // 4dp ≈ 11m. Finer than we can use (provider coords are ZIP-centroid)
        // and coarse enough not to persist a precise home address.
        commit({
          lat: Number(pos.coords.latitude.toFixed(4)),
          lng: Number(pos.coords.longitude.toFixed(4)),
        });
        resolve(current);
      },
      () => { setStatus("denied"); resolve(null); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600_000 },
    );
  }).finally(() => { inflight = null; }) as Promise<UserCoords | null>;
  return inflight;
}

/**
 * Silent site-wide pickup, called from the root on every page load.
 *
 * Deliberately does NOT prompt. It only fetches when the visitor has ALREADY
 * granted permission, so a returning visitor gets distances with no
 * interaction. This used to fire a cold prompt on first paint of whatever page
 * the visitor landed on — usually before they had searched for anything, which
 * is the context most likely to get a "Block". A browser denial is sticky and
 * cannot be re-prompted, so one badly-timed ask costs the feature for good;
 * the ask now happens on intent, via requestUserCoords().
 *
 * navigator.permissions is unavailable on some older Safari builds; there we
 * simply stay idle until the visitor asks, which is the safe direction.
 */
export function ensureUserCoords(): void {
  if (typeof window === "undefined" || !navigator.geolocation) return;
  if (getUserCoords()) return;
  navigator.permissions?.query({ name: "geolocation" })
    .then((p) => { if (p.state === "granted") void locate(); })
    .catch(() => { /* no permissions API — wait for intent */ });
}

/**
 * Explicit "use my location" — prompts if the browser hasn't decided yet.
 * Resolves to the coords, or null if denied/unavailable. Call from a real user
 * gesture; browsers require one to show the prompt.
 */
export function requestUserCoords(): Promise<UserCoords | null> {
  const existing = getUserCoords();
  if (existing) return Promise.resolve(existing);
  return locate();
}

/** Forget this session's coords (the "✕" on the near-me chip). */
export function clearUserCoords(): void {
  current = null;
  hydrated = true;
  status = "idle";
  try { sessionStorage.removeItem(COORDS_KEY); } catch { /* private mode */ }
  notify();
}
